/* External */
import {
  memo, useCallback, useEffect, useState, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

/* Ott-common */
import Title from '@dtvgo/title';
import CardCreate from '@dtvgo/card-create-profile';

/* Components */
import FocusableButtonprofile from '../../../../components/FocusableButtonProfile';

/* Utils */
import { colors, TYPE_ACTIONS_PROFILE } from '../../../../utils/constants';
import { Routes, getLink } from '../../../../utils/router';
import { useCountry } from '../../../../providers/ConfigProvider';
import { useGtmLocalContext } from '../../../../providers/GtmLocalProvider';

/* GTM */
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../../utils/googleTags';

/* Storage */
import { createProfile } from '../../../../storage/profiles/actions';

/* Style */
import './CreateProfileConfirmation.scss';

import { createProfileAPI } from '../../../../services/profiles';

export function checkErrorSource() {
  return 'DEVICE';
}

function CreateProfileConfirmation({ realFocusKey, setFocus }) {
  const createProfileButtonFocusKey = `${realFocusKey}-create-profile-button`;
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const dispatch = useDispatch();
  const userProfiles = useSelector((state) => state.profile.profiles) ?? JSON.parse(localStorage.getItem('profiles'));
  const [profiles] = useState(userProfiles);
  const selectedProfile = profiles?.find((profile) => String(profile?.id) === params.id);
  const cardProfileName = selectedProfile ? selectedProfile.name : location.state?.username;
  const country = useCountry();
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);
  const [buttonLoading, setButtonLoading] = useState(false);

  const profileCommonTagEvent = useCallback(({ eventName }) => {
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.PROFILES_DATA,
        subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_ONBOARDING,
        eventName,
      });
    }
  }, [commonTaggingEvent]);

  useEffect(() => {
    setFocus(createProfileButtonFocusKey);
  }, [createProfileButtonFocusKey, setFocus]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      profileCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PROFILES_ONBOARDING.PAGE_VIEW_CREATE_CONFIRMATION_ONBOARDING,
      });
      tagsMountRef.current = false;
    }
  }, [profileCommonTagEvent, country]);

  const clickCreateProfile = useCallback(() => {
    setButtonLoading(true);
    profileCommonTagEvent({
      eventName: GTM_EVENT_NAMES.PROFILES_ONBOARDING.CONTINUE_CREATE_CONFIRMATION_ONBOARDING,
    });
    createProfileAPI({
      name: location.state?.username,
      avatar: '',
      ageRating: '',
    }).then((res) => {
      dispatch(createProfile(res.data.profiles));
      localStorage.setItem('profiles', JSON.stringify(res.data.profiles));
      history.push(
        getLink(Routes.Private.Profiles),
        {
          create: true,
          typeCreate: TYPE_ACTIONS_PROFILE.ONBOARDING,
        },
      );
      setButtonLoading(false);
      return true;
    })
      .catch((error) => {
        console.error(error);
        history.push(
          getLink(Routes.Private.Profiles.Error),
          {
            create: false,
            source: 'MIDDLEWARE',
            location: 'PROFILE_CREATE',
            type: 'UNEXPECTED',
            externCode: error?.response?.status,
            typeError: TYPE_ACTIONS_PROFILE.CREATE,
          },
        );
        return false;
      });
  }, [dispatch, history, location, profileCommonTagEvent]);

  const editProfileName = useCallback(() => {
    profileCommonTagEvent({
      eventName: GTM_EVENT_NAMES.PROFILES_ONBOARDING.EDIT_NAME_CONFIRMATION_ONBOARDING,
    });
    history.push(
      getLink(Routes.Private.Profiles.Create.Name),
      { typeCreateName: TYPE_ACTIONS_PROFILE.ONBOARDING },
    );
  }, [history, profileCommonTagEvent]);

  return (
    <div className="dtv-html5-c-create-profile-confirmation">
      <div className="dtv-html5-c-create-profile-confirmation--div">
        <h1>
          <FormattedMessage id="profile.create" />
        </h1>
        <div>
          <CardCreate
            color={colors[selectedProfile?.color]}
            isInputNotEmpty={cardProfileName.match(/\b(\w)/g)}
          />
          <Title
            title={`${cardProfileName}!`}
            description={<FormattedMessage id="profile.main.onboarding" />}
            descriptionExists
          />
          <div className="dtv-html5-c-create-profile-confirmation--div-buttonAlignment">
            <FocusableButtonprofile
              type="submit"
              secondGray
              bordered
              onEnterPress={editProfileName}
            >
              <FormattedMessage id="profile.edit" />
            </FocusableButtonprofile>
            <FocusableButtonprofile
              type="submit"
              bordered
              secondGray
              focusKey={createProfileButtonFocusKey}
              onEnterPress={clickCreateProfile}
              disabled={buttonLoading}
            >
              <FormattedMessage id="profile.create" />
            </FocusableButtonprofile>
          </div>
        </div>
      </div>
    </div>
  );
}

CreateProfileConfirmation.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
};

export default memo(withFocusable()(memo(CreateProfileConfirmation)));
